import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, PlayCircle, FileText, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader, ProgressBar, StatusDot } from "@/components/common";
import { NoBizCta } from "@/pages/Journey";
import { actionLink } from "@/pages/Home";

const NEXT = { not_started: "in_progress", in_progress: "completed", completed: "not_started" };

export function ChecklistRow({ item, bizId, onChange, tools = [] }) {
  const manual = item.rule === "manual";
  const cycle = async () => {
    const status = NEXT[item.status] || "completed";
    await api.put(`/businesses/${bizId}/checklist/${encodeURIComponent(item.key)}`, { status });
    onChange(item.key, status);
    if (status === "completed") toast.success(`${item.label} ✓`);
  };
  const recs = item.tool_category ? tools.filter((x) => x.category === item.tool_category) : [];
  return (
    <li className="py-3" data-testid={`checklist-item-${item.key}`}>
      <div className="flex items-center gap-3">
        <StatusDot status={item.status} onClick={manual ? cycle : undefined} testid={`checklist-toggle-${item.key}`} />
        <span className={`flex-1 text-sm ${item.status === "completed" ? "text-[#8C9BA5] line-through" : "font-medium"}`}>{item.label}</span>
        <span className={`text-[10px] font-bold uppercase tracking-wider ${item.status === "completed" ? "text-[#1DB99A]" : item.status === "in_progress" ? "text-[#F59E0B]" : "text-[#8C9BA5]"}`}>
          {item.status.replace("_", " ")}</span>
      </div>
      {!manual && item.status !== "completed" && <div className="ml-10 text-[11px] text-[#8C9BA5]">Completes automatically when you save the lesson action</div>}
      {recs.length > 0 && item.status !== "completed" && (
        <div className="ml-10 mt-2" data-testid={`checklist-tools-${item.key}`}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#8C9BA5] mb-1.5">Recommended {item.tool_category} tools</div>
          <div className="flex flex-wrap gap-2">{recs.map((x) => (
            <a key={x.id} href={x.affiliate_url || x.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-[#EADEC9] bg-white px-2.5 py-1 text-xs font-semibold hover:border-[#1DB99A]">
              <img src={x.logo_url} alt="" className="h-4 w-4 rounded" />{x.name}<ExternalLink className="h-3 w-3 text-[#8C9BA5]" /></a>))}
          </div>
        </div>
      )}
    </li>
  );
}

export default function Stage() {
  const { key } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [allTools, setAllTools] = useState([]);
  useEffect(() => {
    setD(null);
    api.get(`/stages/${encodeURIComponent(key)}`).then((r) => setD(r.data)).catch(() => setErr(true));
    api.get("/tools").then((r) => setAllTools(r.data.tools)).catch(() => {});
  }, [key]);
  if (err) return <NoBizCta />;
  if (!d) return <Loader />;
  const s = d.stage;
  const update = (k, status) => setD((x) => ({ ...x, stage: { ...x.stage, items: x.stage.items.map((i) => i.key === k ? { ...i, status } : i) } }));
  const nextLesson = s.lessons.find((l) => !l.completed);
  return (
    <div data-testid="stage-page">
      <Link to="/journey" className="inline-flex items-center gap-1 text-sm font-semibold text-[#5C6B73] mb-4" data-testid="stage-back-link"><ArrowLeft className="h-4 w-4" /> Journey</Link>
      <section className="rounded-3xl bg-[#1E2B3C] text-white p-6 sm:p-8 fade-up">
        <div className="sb-eyebrow text-[#1DB99A]">Stage {s.order} of 7</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold mt-2" data-testid="stage-name">{s.name}</h1>
        <p className="text-white/70 mt-2">{s.question}</p>
        <div className="flex items-center gap-3 mt-5"><div className="flex-1 h-2.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-[#1DB99A] rounded-full transition-[width] duration-700" style={{ width: `${s.pct}%` }} /></div><span className="font-bold text-sm" data-testid="stage-progress">{s.pct}%</span></div>
        <div className="flex flex-wrap gap-2 mt-5">
          {nextLesson && <Link to={`/lesson/${nextLesson.lesson_id}`} className="sb-btn-primary text-sm" data-testid="stage-continue-btn"><PlayCircle className="h-4 w-4" />{nextLesson.visited ? "Continue" : "Start"}: {nextLesson.title}</Link>}
          {!nextLesson && d.next_action && <Link to={actionLink(d.next_action)} className="sb-btn-primary text-sm" data-testid="stage-next-action-btn">{d.next_action.title}</Link>}
          <Link to={`/stage/${encodeURIComponent(key)}/output`} className="sb-btn text-sm bg-white/10 text-white hover:bg-white/15" data-testid="stage-output-btn"><FileText className="h-4 w-4" />{s.output}</Link>
        </div>
      </section>

      <div className="grid lg:grid-cols-5 gap-5 mt-5">
        <section className="lg:col-span-3 sb-card p-5" data-testid="stage-lessons">
          <div className="flex justify-between items-center mb-2"><div className="sb-eyebrow">Lessons</div><span className="text-xs font-bold">{s.lessons_done}/{s.lessons_total}</span></div>
          <ol className="divide-y divide-[#EADEC9]">
            {s.lessons.map((l, i) => (
              <li key={l.lesson_id}>
                <Link to={`/lesson/${l.lesson_id}`} className="flex items-center gap-3 py-3.5 group" data-testid={`stage-lesson-${l.lesson_id}`}>
                  <span className={`h-9 w-9 shrink-0 rounded-xl flex items-center justify-center text-xs font-bold ${l.completed ? "bg-[#1DB99A] text-white" : l.visited ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-[#F5F0E6] text-[#5C6B73]"}`}>
                    {l.completed ? <Check className="h-4 w-4" strokeWidth={3} /> : i + 1}</span>
                  <div className="flex-1 min-w-0"><div className="font-semibold text-sm group-hover:text-[#179B80] transition-colors">{l.title}</div>
                    <div className="text-[11px] text-[#8C9BA5]">{l.est_min} min · {l.completed ? "Completed" : l.visited ? `${l.visited}/7 screens` : "Not started"}</div></div>
                  <PlayCircle className="h-5 w-5 text-[#8C9BA5] group-hover:text-[#1DB99A]" />
                </Link>
              </li>
            ))}
          </ol>
        </section>
        <div className="lg:col-span-2 space-y-5">
          <section className="sb-card p-5" data-testid="stage-checklist">
            <div className="flex justify-between items-center"><div className="sb-eyebrow">{s.name} checklist</div><span className="text-xs font-bold">{s.items.filter((i) => i.status === "completed").length}/{s.items.length}</span></div>
            <ul className="divide-y divide-[#EADEC9]">{s.items.map((i) => <ChecklistRow key={i.key} item={i} bizId={d.business_id} onChange={update} tools={allTools} />)}</ul>
          </section>
          {d.templates.length > 0 && (
            <section className="sb-card p-5" data-testid="stage-templates">
              <div className="sb-eyebrow mb-3">Templates for this stage</div>
              {d.templates.map((tp) => <Link key={tp.id} to={`/resources#${tp.id}`} className="flex items-center gap-2 py-2 text-sm font-semibold hover:text-[#179B80]"><FileText className="h-4 w-4 text-[#1DB99A]" />{tp.title}</Link>)}
            </section>
          )}
          {d.tools.length > 0 && (
            <section className="sb-card p-5" data-testid="stage-tools">
              <div className="sb-eyebrow mb-3">Recommended tools</div>
              <div className="flex flex-wrap gap-2">{d.tools.map((x) => <a key={x.id} href={x.affiliate_url || x.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-[#EADEC9] px-3 py-1.5 text-xs font-semibold hover:border-[#1DB99A]"><img src={x.logo_url} alt="" className="h-4 w-4 rounded" />{x.name}</a>)}</div>
            </section>
          )}
          <Link to="/coach" className="sb-card p-5 flex items-center gap-3 hover:shadow-md transition-shadow" data-testid="stage-ask-coach">
            <Sparkles className="h-5 w-5 text-[#1DB99A]" /><div className="text-sm"><b>Stuck?</b> Ask Sidebizz about this stage.</div>
          </Link>
        </div>
      </div>
    </div>
  );
}
